import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Container = styled.div`
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;
  background: linear-gradient(135deg, #f5f7fa 0%, #f0f4ff 100%);
  min-height: 100vh;
`;

const Header = styled.div`
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  gap: 20px;
  
  img {
    width: 64px;
    height: 64px;
    border-radius: 12px;
  }
  
  .text h1 {
    font-size: 2rem;
    color: #0a0e27;
    margin: 0 0 4px;
    font-weight: 900;
    background: linear-gradient(135deg, #0a0e27 0%, #2d5a9e 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .text p {
    color: #556b7d;
    margin: 0;
    font-weight: 500;
    font-size: 1.05rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 28px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(245, 249, 255, 0.92) 100%);
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.1);
  height: fit-content;
  border: 2px solid rgba(74, 144, 226, 0.08);
  backdrop-filter: blur(20px);
`;

const MainContent = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 249, 255, 0.95) 100%);
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 16px 48px rgba(74, 144, 226, 0.12);
  border: 2px solid rgba(74, 144, 226, 0.08);
  backdrop-filter: blur(30px);
`;

const SectionTitle = styled.h2`
  font-size: 1.15rem;
  margin-bottom: 18px;
  color: #2d5a9e;
  font-weight: 800;
  letter-spacing: 0.3px;
`;

const TemplateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TemplateItem = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  cursor: pointer;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, #4a90e2 0%, #2d5a9e 100%)' 
    : 'linear-gradient(135deg, rgba(74, 144, 226, 0.08) 0%, rgba(74, 144, 226, 0.04) 100%)'};
  border: 2px solid ${props => props.active ? '#2d5a9e' : 'rgba(74, 144, 226, 0.15)'};
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  &:hover {
    background: ${props => props.active 
      ? 'linear-gradient(135deg, #5ba3f5 0%, #3d6bb1 100%)' 
      : 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(74, 144, 226, 0.08) 100%)'};
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.15);
  }
  
  h4 {
    margin: 0 0 4px;
    font-size: 0.95rem;
    color: ${props => props.active ? 'white' : '#2d5a9e'};
    font-weight: 700;
  }
  
  p {
    margin: 0;
    font-size: 0.8rem;
    color: ${props => props.active ? 'rgba(255,255,255,0.8)' : '#8a96aa'};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 10px;
    font-weight: 700;
    color: #2d5a9e;
    font-size: 0.95rem;
    letter-spacing: 0.2px;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 14px 16px;
    border: 2px solid rgba(74, 144, 226, 0.15);
    border-radius: 10px;
    font-size: 1rem;
    font-family: inherit;
    box-sizing: border-box;
    background: linear-gradient(135deg, rgba(240, 247, 255, 0.6) 0%, rgba(232, 244, 255, 0.4) 100%);
    color: #0a0e27;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #4a90e2;
      box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
      background: rgba(255, 255, 255, 0.9);
    }
    
    &::placeholder {
      color: #8a96aa;
    }
  }
  
  textarea {
    min-height: 240px;
    resize: vertical;
    font-family: 'Monaco', 'Menlo', monospace;
  }
  
  small {
    display: block;
    margin-top: 8px;
    color: #8a96aa;
    font-size: 0.85rem;
    font-weight: 500;
  }
`;

const Button = styled.button`
  padding: 14px 28px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  border: none;
  margin-right: 12px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  letter-spacing: 0.3px;
  
  ${props => props.primary && `
    background: linear-gradient(135deg, #4a90e2 0%, #2d5a9e 100%);
    color: white;
    box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 12px 36px rgba(74, 144, 226, 0.4);
      background: linear-gradient(135deg, #5ba3f5 0%, #3d6bb1 100%);
    }
  `}
  
  ${props => props.secondary && `
    background: linear-gradient(135deg, rgba(74, 144, 226, 0.12) 0%, rgba(74, 144, 226, 0.06) 100%);
    color: #4a90e2;
    border: 2px solid rgba(74, 144, 226, 0.2);
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, rgba(74, 144, 226, 0.18) 0%, rgba(74, 144, 226, 0.09) 100%);
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.success && `
    background: linear-gradient(135deg, #00C853 0%, #009624 100%);
    color: white;
    box-shadow: 0 8px 24px rgba(0, 200, 83, 0.3);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 12px 36px rgba(0, 200, 83, 0.4);
      background: linear-gradient(135deg, #00e060 0%, #00a030 100%);
    }
  `}
  
  &:active:not(:disabled) {
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PractitionerSelect = styled.div`
  max-height: 280px;
  overflow-y: auto;
  border: 2px solid rgba(74, 144, 226, 0.15);
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(240, 247, 255, 0.4) 0%, rgba(232, 244, 255, 0.2) 100%);
  
  .item {
    padding: 12px 16px;
    cursor: pointer;
    border-bottom: 1px solid rgba(74, 144, 226, 0.08);
    transition: all 0.2s ease;
    
    &:hover {
      background: linear-gradient(135deg, rgba(74, 144, 226, 0.08) 0%, rgba(74, 144, 226, 0.04) 100%);
    }
    
    &.selected {
      background: linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(74, 144, 226, 0.08) 100%);
      border-left: 3px solid #4a90e2;
      padding-left: 13px;
    }
    
    &:last-child {
      border-bottom: none;
    }
    
    .name {
      font-weight: 700;
      color: #2d5a9e;
      margin-bottom: 2px;
    }
    
    .email {
      font-size: 0.8rem;
      color: #8a96aa;
      font-weight: 500;
    }
  }
`;

const Preview = styled.div`
  background: linear-gradient(135deg, rgba(240, 247, 255, 0.6) 0%, rgba(232, 244, 255, 0.4) 100%);
  border: 2px solid rgba(74, 144, 226, 0.15);
  border-radius: 10px;
  padding: 24px;
  margin-top: 24px;
  
  h3 {
    margin: 0 0 16px;
    color: #2d5a9e;
    font-size: 0.95rem;
    text-transform: uppercase;
    font-weight: 800;
    letter-spacing: 1px;
  }
  
  .preview-content {
    background: white;
    padding: 24px;
    border-radius: 10px;
    border: 2px solid rgba(74, 144, 226, 0.1);
  }
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 2px solid rgba(74, 144, 226, 0.15);
  margin-bottom: 32px;
  gap: 4px;
`;

const Tab = styled.button`
  padding: 14px 24px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 800;
  color: ${props => props.active ? '#4a90e2' : '#8a96aa'};
  border-bottom: 3px solid ${props => props.active ? '#4a90e2' : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.3s ease;
  letter-spacing: 0.3px;
  
  &:hover {
    color: #4a90e2;
  }
`;

const EmailManagement = () => {
  const [activeTab, setActiveTab] = useState('compose');
  const [templates, setTemplates] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [sending, setSending] = useState(false);
  
  const [emailForm, setEmailForm] = useState({
    to: '',
    subject: '',
    content: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templatesRes, practitionersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/email/templates`),
        axios.get(`${API_URL}/admin/practitioners?limit=100`)
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setPractitioners(practitionersRes.data.practitioners || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setEmailForm({
      ...emailForm,
      subject: template.subject,
      content: template.content
    });
  };

  const selectPractitioner = (practitioner) => {
    setSelectedPractitioner(practitioner);
    setEmailForm({
      ...emailForm,
      to: practitioner.email
    });
    
    // Replace template variables
    if (emailForm.content) {
      let content = emailForm.content;
      content = content.replace(/\{\{name\}\}/g, `${practitioner.firstName} ${practitioner.lastName}`);
      content = content.replace(/\{\{firstName\}\}/g, practitioner.firstName);
      content = content.replace(/\{\{lastName\}\}/g, practitioner.lastName);
      content = content.replace(/\{\{licenseNumber\}\}/g, practitioner.licenseNumber || 'N/A');
      content = content.replace(/\{\{expiryDate\}\}/g, 
        practitioner.licenseExpiry ? new Date(practitioner.licenseExpiry).toLocaleDateString() : 'N/A'
      );
      setEmailForm(prev => ({ ...prev, content }));
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.to || !emailForm.subject || !emailForm.content) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      setSending(true);
      
      await axios.post(`${API_URL}/admin/email/send`, {
        to: emailForm.to,
        subject: emailForm.subject,
        content: emailForm.content
      });
      
      toast.success('Email sent successfully!');
      
      // Reset form
      setEmailForm({ to: '', subject: '', content: '' });
      setSelectedPractitioner(null);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Send email error:', error);
      toast.error(error.response?.data?.error || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEmailForm(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Container>
      <Header>
        <img src="/logo192.png" alt="Vision Analytics Logo" />
        <div className="text">
          <h1>Vision Analytics</h1>
          <p>Email Management & Communications</p>
        </div>
      </Header>
      
      <Tabs>
        <Tab active={activeTab === 'compose'} onClick={() => setActiveTab('compose')}>
          ✍️ Compose Email
        </Tab>
        <Tab active={activeTab === 'templates'} onClick={() => setActiveTab('templates')}>
          📋 Templates
        </Tab>
      </Tabs>
      
      {activeTab === 'compose' && (
        <Grid>
          <Sidebar>
            <SectionTitle>📋 Quick Templates</SectionTitle>
            <TemplateList>
              {templates.map(template => (
                <TemplateItem
                  key={template.id}
                  active={selectedTemplate?.id === template.id}
                  onClick={() => selectTemplate(template)}
                >
                  <h4>{template.name}</h4>
                  <p>{template.subject.substring(0, 40)}...</p>
                </TemplateItem>
              ))}
              {templates.length === 0 && (
                <div style={{ padding: '12px', color: '#8a96aa', fontSize: '0.9rem', fontWeight: '500' }}>
                  No templates available
                </div>
              )}
            </TemplateList>
            
            <SectionTitle style={{ marginTop: '28px' }}>👤 Select Recipient</SectionTitle>
            <PractitionerSelect>
              {practitioners.map(p => (
                <div
                  key={p.id}
                  className={`item ${selectedPractitioner?.id === p.id ? 'selected' : ''}`}
                  onClick={() => selectPractitioner(p)}
                >
                  <div className="name">{p.firstName} {p.lastName}</div>
                  <div className="email">{p.email}</div>
                </div>
              ))}
              {practitioners.length === 0 && (
                <div className="item" style={{ color: '#8a96aa', fontWeight: '500' }}>No practitioners found</div>
              )}
            </PractitionerSelect>
          </Sidebar>
          
          <MainContent>
            <SectionTitle>Compose Email</SectionTitle>
            
            <FormGroup>
              <label>To (Email Address) *</label>
              <input
                type="email"
                name="to"
                value={emailForm.to}
                onChange={handleFormChange}
                placeholder="recipient@email.com"
              />
            </FormGroup>
            
            <FormGroup>
              <label>Subject *</label>
              <input
                type="text"
                name="subject"
                value={emailForm.subject}
                onChange={handleFormChange}
                placeholder="Email subject"
              />
            </FormGroup>
            
            <FormGroup>
              <label>Content (HTML supported) *</label>
              <textarea
                name="content"
                value={emailForm.content}
                onChange={handleFormChange}
                placeholder="Write your email content here. You can use HTML for formatting."
              />
              <small>
                Available variables: {'{{name}}'}, {'{{firstName}}'}, {'{{lastName}}'}, {'{{licenseNumber}}'}, {'{{expiryDate}}'}
              </small>
            </FormGroup>
            
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Button 
                success 
                onClick={handleSendEmail}
                disabled={sending || !emailForm.to || !emailForm.subject || !emailForm.content}
              >
                {sending ? '📤 Sending...' : '📤 Send Email'}
              </Button>
              
              <Button 
                secondary 
                onClick={() => {
                  setEmailForm({ to: '', subject: '', content: '' });
                  setSelectedPractitioner(null);
                  setSelectedTemplate(null);
                }}
              >
                🗑️ Clear
              </Button>
            </div>
            
            {emailForm.content && (
              <Preview>
                <h3>📧 Email Preview</h3>
                <div 
                  className="preview-content"
                  dangerouslySetInnerHTML={{ __html: emailForm.content }}
                />
              </Preview>
            )}
          </MainContent>
        </Grid>
      )}
      
      {activeTab === 'templates' && (
        <MainContent>
          <SectionTitle>📋 Email Templates</SectionTitle>
          <p style={{ color: '#556b7d', marginBottom: '28px', fontWeight: '500', lineHeight: '1.6' }}>
            These templates are automatically used when approving/rejecting practitioners.
            You can also use them as starting points for custom emails.
          </p>
          
          {templates.map(template => (
            <div key={template.id} style={{ 
              background: 'linear-gradient(135deg, rgba(240, 247, 255, 0.6) 0%, rgba(232, 244, 255, 0.4) 100%)',
              borderRadius: '10px', 
              padding: '24px', 
              marginBottom: '20px',
              border: '2px solid rgba(74, 144, 226, 0.1)'
            }}>
              <h3 style={{ margin: '0 0 8px', color: '#2d5a9e', fontSize: '1.1rem', fontWeight: '800' }}>{template.name}</h3>
              <p style={{ margin: '0 0 12px', color: '#556b7d', fontWeight: '600' }}>
                <strong>Subject:</strong> {template.subject}
              </p>
              <div 
                style={{ 
                  background: 'white', 
                  padding: '20px', 
                  borderRadius: '10px',
                  border: '2px solid rgba(74, 144, 226, 0.08)',
                  marginBottom: '12px'
                }}
                dangerouslySetInnerHTML={{ __html: template.content }}
              />
              <Button 
                primary
                onClick={() => {
                  selectTemplate(template);
                  setActiveTab('compose');
                }}
              >
                ✏️ Use This Template
              </Button>
            </div>
          ))}
          
          {templates.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#8a96aa'
            }}>
              <p style={{ fontSize: '1.1rem', fontWeight: '600' }}>No templates available</p>
            </div>
          )}
        </MainContent>
      )}
    </Container>
  );
};

export default EmailManagement;